import { Field } from '@campfire/field/lib';
import { TabletButton } from '@campfire/tablet-button/lib';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@material-ui/core';
import { FieldArrayRenderProps, Form, Formik, useFormikContext } from 'formik';
import React, { memo, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { object as YupObject, string as YupString, number as YupNumber } from 'yup';
import { FormBuilderValues } from '../FormBuilder';
import { RepeatableGroupFormItemData } from '../FormBuilderDialogContext';

const validationSchema = YupObject().shape({
  title: YupString().required('Please provide a section title (e.g. "References")'),
  label: YupString().required('Please provide a label (e.g. "reference", "entry")'),
  minInstances: YupNumber()
    .min(0)
    .max(50)
    .required(),
  maxInstances: YupNumber()
    .min(1)
    .max(50)
    .required(),
});

interface Props {
  open: boolean;
  close: () => void;
  arrayHelpers: FieldArrayRenderProps;
  data: RepeatableGroupFormItemData | null;
}

type RepeatableGroupValues = {
  formItemId: string;
  title: string;
  description: string;
  label: string;
  minInstances: number;
  maxInstances: number;
  order: number;
  __typename: 'VOLUNTEER_FormItemRepeatableGroupType';
  groupItems: any[];
};

export const RepeatableGroupFormItemDialog = memo((props: Props) => {
  const { open, close, data } = props;
  const formikContext = useFormikContext<FormBuilderValues>();
  const { values } = formikContext;
  const { formItems } = values;

  const title = useMemo(() => (data ? 'Edit Repeatable Section' : 'Add Repeatable Section'), [data]);

  const initialValues: RepeatableGroupValues = useMemo(
    () => ({
      title: data?.title ?? '',
      description: data?.description ?? '',
      label: data?.label ?? '',
      minInstances: data?.minInstances ?? 1,
      maxInstances: data?.maxInstances ?? 10,
      order: data?.order ?? formikContext.values.formItems.allIds.length,
      formItemId: data?.formItemId ?? `new-${uuidv4()}`,
      __typename: 'VOLUNTEER_FormItemRepeatableGroupType' as const,
      groupItems: data?.groupItems ?? [],
    }),
    [data, open]
  );

  const handleSubmit = (vals: RepeatableGroupValues) => {
    return data ? editGroup(vals) : addGroup(vals);
  };

  const addGroup = (vals: RepeatableGroupValues) => {
    formikContext.setFieldValue('formItems', {
      ...formItems,
      allIds: formItems.allIds.concat(vals.formItemId),
      byId: {
        ...formItems.byId,
        [vals.formItemId]: vals,
      },
      byGroup: {
        ...formItems.byGroup,
        [vals.formItemId]: [],
      },
    });
    close();
  };

  const editGroup = (vals: RepeatableGroupValues) => {
    formikContext.setFieldValue('formItems', {
      ...formItems,
      byId: {
        ...formItems.byId,
        [vals.formItemId]: vals,
      },
    });
    close();
  };

  const removeGroup = () => {
    const groupId = initialValues.formItemId;
    const childIds = formItems.byGroup[groupId] || [];
    const updatedByGroupItem = { ...formItems.byGroupItem };
    childIds.forEach((childId) => {
      delete updatedByGroupItem[childId];
    });
    const updatedByGroup = { ...formItems.byGroup };
    delete updatedByGroup[groupId];
    const optionId = formItems.byTriggerOption[groupId];
    formikContext.setFieldValue('formItems', {
      ...formItems,
      allIds: formItems.allIds.filter((itemId) => itemId !== groupId),
      byGroup: updatedByGroup,
      byGroupItem: updatedByGroupItem,
      byTrigger: optionId
        ? {
            ...formItems.byTrigger,
            [optionId]: {
              ...formItems.byTrigger[optionId],
              triggerItemIds: formItems.byTrigger[optionId].triggerItemIds.filter((itemId) => itemId !== groupId),
            },
          }
        : formItems.byTrigger,
    });
    close();
  };

  return (
    <Dialog open={open} maxWidth='sm' fullWidth>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ submitForm }) => (
          <Form>
            <DialogTitle>
              <Typography variant='h6'>{title}</Typography>
              <Typography>
                Allow respondents to repeat a set of questions. For example, entering details for multiple children.
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Field
                name='title'
                label='Section title'
                required
                fullWidth
                variant='outlined'
                helper='Displayed as the section heading (e.g. "References")'
                autoFocus
                style={{ marginBottom: 16 }}
              />
              <Field
                name='description'
                label='Description (optional)'
                fullWidth
                variant='outlined'
                helper='Shown below the section title to provide instructions'
                style={{ marginBottom: 16 }}
              />
              <Field
                name='label'
                label='Item label'
                required
                fullWidth
                variant='outlined'
                helper='Used in the "Add another..." button (e.g. "reference", "entry")'
                style={{ marginBottom: 16 }}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Field
                    name='minInstances'
                    label='Minimum'
                    type='number'
                    required
                    fullWidth
                    variant='outlined'
                    helper='Minimum number of entries'
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    name='maxInstances'
                    label='Maximum'
                    type='number'
                    required
                    fullWidth
                    variant='outlined'
                    helper='Maximum number of entries'
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid container justify='flex-end'>
                {data ? (
                  <Grid item xs>
                    <TabletButton variant='outlined' color='error' onClick={removeGroup}>
                      Delete
                    </TabletButton>
                  </Grid>
                ) : null}
                <Grid item>
                  <TabletButton onClick={close}>Cancel</TabletButton>
                </Grid>
                <Grid item>
                  <TabletButton type='button' color='primary' variant='contained' onClick={submitForm}>
                    Done
                  </TabletButton>
                </Grid>
              </Grid>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
});
